import { useState } from 'react';

import type { TicketBaselineCandidate, TicketDetail } from '@nightshift/ui-types';

import { Spinner } from '../../components/primitives.tsx';
import './baselines.css';

function Shot({ image, label }: { image: TicketBaselineCandidate['actual'] | null; label: string }) {
  if (!image) {
    return (
      <figure className="baseline-shot is-missing">
        <figcaption className="dim">{label}: not uploaded</figcaption>
      </figure>
    );
  }
  return (
    <figure className="baseline-shot">
      <a href={`/api/artifacts/${image.artifactId}/file`} target="_blank" rel="noreferrer">
        <img src={`/api/artifacts/${image.artifactId}/file`} alt={`${label} ${image.artifactPath}`} loading="lazy" />
      </a>
      <figcaption className="dim">
        {label} · <span className="mono">{image.artifactPath.split('/').pop()}</span> · sha256 {image.sha256.slice(0, 12)}
      </figcaption>
    </figure>
  );
}

/**
 * Expected, actual and difference side by side, per failed scenario, with a checkbox each.
 *
 * Every image is the file Nightshift stored from the failed job; nothing here is regenerated.
 * The approve button names exactly the scenarios ticked, and the server refuses the request if
 * the head has moved since the pictures were taken.
 */
export function BaselineUpdatePanel({
  detail,
  busy,
  onCancel,
  onApprove,
}: {
  detail: TicketDetail;
  busy: boolean;
  onCancel: () => void;
  onApprove: (scenarios: string[], note: string) => void;
}) {
  const { baselines } = detail;
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [note, setNote] = useState('');

  if (!baselines.configured) {
    return (
      <div className="rise baseline-panel">
        <p>This project declares no <code>[verification.baselines]</code>, so Nightshift does not know where a baseline lives.</p>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Close</button>
      </div>
    );
  }
  if (baselines.candidates.length === 0) {
    return (
      <div className="rise baseline-panel">
        <p>No failed capture scenario at the current head has an actual image to approve.</p>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Close</button>
      </div>
    );
  }

  function toggle(scenario: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(scenario)) next.delete(scenario);
      else next.add(scenario);
      return next;
    });
  }

  return (
    <section className="rise baseline-panel" aria-label="Approve visual baselines">
      <p className="drawer-section-kicker">Visual baseline update at {baselines.headSha?.slice(0, 8)}</p>
      <p className="dim">
        Approve a scenario only when its actual image is what the ticket set out to produce. Approving copies
        the stored actual bytes to the baseline path on the ticket branch and runs a fresh pipeline; thresholds
        and every other baseline stay as they are.
      </p>
      {!baselines.offered && baselines.reason ? <p className="form-error">{baselines.reason}</p> : null}
      <ul className="baseline-list">
        {baselines.candidates.map((candidate) => (
          <li key={candidate.scenario} className="baseline-candidate" data-scenario={candidate.scenario}>
            <label className="baseline-candidate-head">
              <input
                type="checkbox"
                checked={selected.has(candidate.scenario)}
                disabled={busy || !baselines.offered}
                onChange={() => toggle(candidate.scenario)}
              />
              <strong>{candidate.scenario}</strong>
              <span className="mono dim">→ {candidate.expectedPath}</span>
              <span className="dim">job {candidate.jobId}{candidate.viewport ? ` · ${candidate.viewport}` : ''}</span>
            </label>
            {candidate.message ? <p className="dim baseline-message">{candidate.message}</p> : null}
            <div className="baseline-shots">
              <Shot image={candidate.expected} label="Expected" />
              <Shot image={candidate.actual} label="Actual" />
              <Shot image={candidate.diff} label="Difference" />
            </div>
          </li>
        ))}
      </ul>
      <label className="baseline-note">
        <span className="dim">Why these pictures are right (recorded with the decision)</span>
        <input value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} />
      </label>
      <div className="baseline-actions">
        <button
          type="button"
          className="btn btn-accent btn-sm"
          disabled={busy || selected.size === 0 || !baselines.offered}
          onClick={() => onApprove([...selected], note.trim())}
        >
          {busy ? <Spinner /> : null} Approve {selected.size === 1 ? '1 baseline' : `${selected.size} baselines`}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </section>
  );
}
